/*
 * Cart "You might also like" slider (snippets/cart-also-like.liquid).
 *
 * Products picked in the theme settings are rendered first by Liquid. This
 * hides any that are already in the cart, then fills the remaining spots (8
 * in total) with Shopify's related-product recommendations for the items in
 * the cart (products often bought together with them, plus similar ones),
 * fetched through sections/api-cart-recommendations.liquid. It refreshes
 * whenever the cart changes.
 *
 * In the cart drawer nothing is fetched until the drawer is opened or the
 * cart changes, so ordinary page loads aren't slowed down.
 */
(function () {
  if (!window.customElements || window.customElements.get('cart-also-like')) return;

  var SEED_PRODUCTS = 3; // cart products the recommendations are based on
  var PER_PRODUCT = 10; // recommendations requested for each of them

  class CartAlsoLike extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cs-track]');
      this.slider = this.querySelector('complementary-slider');
      if (!this.track) return;

      this.limit = parseInt(this.getAttribute('data-limit'), 10) || 8;
      this.url = this.getAttribute('data-url');
      this.cardSizes = this.getAttribute('data-card-sizes');
      this.cartRoot = this.closest('[data-cart-drawer], [data-cart-page]') || document;
      this.cache = {};
      this.requestId = 0;
      this.signature = null;
      this.active = !this.closest('[data-cart-drawer]');

      this.onCartOpen = this.onCartOpen.bind(this);
      this.onCartChange = this.onCartChange.bind(this);
      document.addEventListener('theme:cart:open', this.onCartOpen);
      document.addEventListener('theme:cart:change', this.onCartChange);

      if (this.active) this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:open', this.onCartOpen);
      document.removeEventListener('theme:cart:change', this.onCartChange);
      clearTimeout(this.timer);
    }

    onCartOpen() {
      this.active = true;
      this.refresh();
    }

    onCartChange() {
      this.active = true;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refresh(), 50);
    }

    // Products in the cart, read from the cart's line items (the theme keeps
    // these up to date). Gift wrap and free items aren't used as the basis
    // for recommendations.
    readCart() {
      var inCart = {};
      var seeds = [];
      var items = this.cartRoot.querySelectorAll('[data-items-holder] [data-item-product-id]');
      Array.prototype.forEach.call(items, function (item) {
        var id = item.getAttribute('data-item-product-id');
        if (!id) return;
        var seedable = !item.classList.contains('giftProduct') && item.getAttribute('data-item-price') !== '0';
        if (seedable && !inCart[id] && seeds.length < SEED_PRODUCTS) seeds.push(id);
        inCart[id] = true;
      });
      return { inCart: inCart, seeds: seeds, hasItems: items.length > 0 };
    }

    refresh() {
      if (!this.active) return;
      var cart = this.readCart();
      var exclude = Object.assign({}, cart.inCart);
      var shown = 0;
      this.hasItems = cart.hasItems;

      // Picked products first, minus any already in the cart.
      Array.prototype.forEach.call(this.track.querySelectorAll('[data-cal-manual]'), (card) => {
        var id = card.getAttribute('data-cs-product-id');
        var hide = Boolean(cart.inCart[id]) || shown >= this.limit;
        card.hidden = hide;
        if (!hide) shown += 1;
        exclude[id] = true;
      });

      var slots = this.limit - shown;
      var signature = [cart.seeds.join(','), Object.keys(cart.inCart).sort().join(','), slots].join('|');
      if (signature === this.signature) {
        this.toggle();
        return;
      }
      this.signature = signature;

      var requestId = ++this.requestId;
      if (!cart.hasItems || slots <= 0 || !cart.seeds.length || !this.url) {
        this.renderRecs([]);
        return;
      }

      this.failed = false;
      Promise.all(cart.seeds.map((id) => this.load(id))).then((lists) => {
        if (requestId !== this.requestId) return;
        // Take from each cart product's list in turn so every item in the
        // cart is represented, skipping duplicates and what's in the cart.
        var picked = [];
        var longest = Math.max.apply(null, lists.map((list) => list.length));
        for (var i = 0; i < longest && picked.length < slots; i++) {
          for (var j = 0; j < lists.length && picked.length < slots; j++) {
            var rec = lists[j][i];
            if (!rec || exclude[rec.id]) continue;
            exclude[rec.id] = true;
            picked.push(rec);
          }
        }
        // Try again next time if a request failed.
        if (this.failed) this.signature = null;
        this.renderRecs(picked);
      });
    }

    load(id) {
      if (!this.cache[id]) {
        var url = this.url + (this.url.indexOf('?') === -1 ? '?' : '&') +
          'section_id=api-cart-recommendations&product_id=' + encodeURIComponent(id) +
          '&limit=' + PER_PRODUCT + '&intent=related';

        this.cache[id] = fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((html) => {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            return Array.prototype.map.call(doc.querySelectorAll('[data-cal-api] > .cs-card[data-cs-product-id]'), (card) => ({
              id: card.getAttribute('data-cs-product-id'),
              html: card.outerHTML
            }));
          })
          .catch(() => {
            delete this.cache[id];
            this.failed = true;
            return [];
          });
      }
      return this.cache[id];
    }

    renderRecs(picked) {
      var current = Array.prototype.slice.call(this.track.querySelectorAll('[data-cal-rec]'));
      var unchanged = current.length === picked.length && current.every((card, index) => card.getAttribute('data-cs-product-id') === picked[index].id);

      if (!unchanged) {
        current.forEach((card) => card.remove());
        var fragment = document.createDocumentFragment();
        picked.forEach((rec) => {
          var template = document.createElement('template');
          template.innerHTML = rec.html;
          var card = template.content.firstElementChild;
          if (!card) return;
          card.setAttribute('data-cal-rec', '');
          if (this.cardSizes) {
            card.querySelectorAll('img[sizes]').forEach((img) => img.setAttribute('sizes', this.cardSizes));
          }
          fragment.appendChild(template.content);
        });
        this.track.appendChild(fragment);
      }

      this.toggle();
    }

    toggle() {
      var hasCards = Boolean(this.track.querySelector('.cs-card:not([hidden])'));
      this.hidden = !(this.hasItems && hasCards);
      if (this.slider && typeof this.slider.update === 'function') this.slider.update();
    }
  }

  window.customElements.define('cart-also-like', CartAlsoLike);
})();
